/**
 * Hook personalizado para validación de distribuidores en tiempo real
 */

import { useState, useCallback, useMemo } from 'react';
import {
  validateDistributorForm,
  validateField,
  getValidationSuggestions
} from '../utils/distributorValidation';

/**
 * Hook para manejar validación de formularios de distribuidores
 * @param {Object} initialData - Datos iniciales del formulario
 * @returns {Object} Objeto con estado y funciones de validación
 */
export const useDistributorValidation = (initialData = {}) => {
  // Estado para errores de validación
  const [errors, setErrors] = useState({});
  
  // Estado para campos que han sido tocados (para mostrar errores solo después de interacción)
  const [touchedFields, setTouchedFields] = useState({});
  
  // Estado para indicar si se está validando
  const [isValidating, setIsValidating] = useState(false);

  /**
   * Valida un campo individual
   * @param {string} fieldName - Nombre del campo
   * @param {string} value - Valor del campo
   * @param {boolean} showError - Si mostrar el error inmediatamente
   */
  const validateSingleField = useCallback((fieldName, value, showError = true) => {
    const validation = validateField(fieldName, value);
    
    setErrors(prevErrors => ({
      ...prevErrors,
      [fieldName]: validation.isValid ? null : validation.error
    }));
    
    if (showError) {
      setTouchedFields(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
    
    return validation;
  }, []);

  /**
   * Valida todo el formulario
   * @param {Object} formData - Datos del formulario
   * @returns {Object} Resultado de validación
   */
  const validateForm = useCallback((formData) => {
    setIsValidating(true);
    
    const validation = validateDistributorForm(formData);
    
    setErrors(validation.errors);
    
    if (!validation.isValid) {
      const allFields = Object.keys(validation.errors);
      setTouchedFields(prev => {
        const newTouched = { ...prev };
        allFields.forEach(field => {
          newTouched[field] = true;
        });
        return newTouched;
      });
    }
    
    setIsValidating(false);
    
    return validation;
  }, []);

  /**
   * Marca un campo como tocado
   * @param {string} fieldName - Nombre del campo
   */
  const touchField = useCallback((fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  /**
   * Limpia los errores de un campo específico
   * @param {string} fieldName - Nombre del campo
   */
  const clearFieldError = useCallback((fieldName) => {
    setErrors(prevErrors => ({
      ...prevErrors,
      [fieldName]: null
    }));
  }, []);

  /**
   * Limpia todos los errores
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
    setTouchedFields({});
  }, []);

  /**
   * Obtiene el error de un campo si ha sido tocado
   * @param {string} fieldName - Nombre del campo
   * @returns {string|null} Error del campo o null
   */
  const getFieldError = useCallback((fieldName) => {
    return touchedFields[fieldName] ? errors[fieldName] : null;
  }, [errors, touchedFields]);

  /**
   * Verifica si un campo tiene error
   * @param {string} fieldName - Nombre del campo
   * @returns {boolean} True si el campo tiene error
   */
  const hasFieldError = useCallback((fieldName) => {
    return Boolean(touchedFields[fieldName] && errors[fieldName]);
  }, [errors, touchedFields]);

  /**
   * Obtiene las sugerencias de validación
   * @param {Object} formData - Datos del formulario
   * @returns {Array} Array de sugerencias
   */
  const getSuggestions = useCallback((formData) => {
    return getValidationSuggestions(formData);
  }, []);

  // Memoizar el estado de validación general
  const isFormValid = useMemo(() => {
    return Object.values(errors).every(error => !error);
  }, [errors]);

  // Memoizar la cantidad de errores
  const errorCount = useMemo(() => {
    return Object.values(errors).filter(error => error).length;
  }, [errors]);

  // Memoizar los campos con errores
  const fieldsWithErrors = useMemo(() => {
    return Object.keys(errors).filter(field => errors[field]);
  }, [errors]);

  return {
    // Estado
    errors,
    touchedFields,
    isValidating,
    isFormValid,
    errorCount,
    fieldsWithErrors,
    
    // Funciones de validación
    validateSingleField,
    validateForm,
    
    // Funciones de manejo de estado
    touchField,
    clearFieldError,
    clearAllErrors,
    
    // Funciones de consulta
    getFieldError,
    hasFieldError,
    getSuggestions
  };
};

/**
 * Hook simplificado para validación en tiempo real de un campo
 * @param {string} fieldName - Nombre del campo
 * @param {string} initialValue - Valor inicial
 * @returns {Object} Objeto con estado y funciones del campo
 */
export const useFieldValidation = (fieldName, initialValue = '') => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);

  const validate = useCallback((newValue = value) => {
    const validation = validateField(fieldName, newValue);
    setError(validation.isValid ? null : validation.error);
    return validation;
  }, [fieldName, value]);

  const handleChange = useCallback((newValue) => {
    setValue(newValue);
    if (touched) {
      validate(newValue);
    }
  }, [touched, validate]);

  const handleBlur = useCallback(() => {
    setTouched(true);
    validate();
  }, [validate]);

  const clear = useCallback(() => {
    setValue('');
    setError(null);
    setTouched(false);
  }, []);

  return {
    value,
    error: touched ? error : null,
    touched,
    isValid: !error,
    setValue: handleChange,
    onBlur: handleBlur,
    validate,
    clear
  };
};

export default useDistributorValidation;